/**
 * v3.99ai — Phase 1 P2: Forge Index display panel.
 *
 * Renders the live Forge Index score + signal breakdown for a strain
 * (`scope="strain"`) or a breeder (`scope="breeder"`). Hides itself
 * cleanly when no data exists yet (so empty strains don't show a 0-score
 * tile that reads negatively).
 *
 * Bloomberg-terminal aesthetic — mono, dense, no chrome, no emojis.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { Gauge, ShieldCheck, AlertTriangle, Sprout, TrendingUp } from "lucide-react";

const SCOPE_PATH = {
  strain:  (key) => `/forge-index/strain/${encodeURIComponent(key)}`,
  breeder: (key) => `/forge-index/breeder/${encodeURIComponent(key)}`,
};

function tierFromScore(s) {
  if (s >= 80) return { label: "Elite",     color: "var(--gold)" };
  if (s >= 60) return { label: "Strong",    color: "var(--primary-text)" };
  if (s >= 40) return { label: "Building",  color: "#7CC9FF" };
  if (s >= 20) return { label: "Emerging",  color: "var(--text-2)" };
  return         { label: "Insufficient data", color: "var(--text-muted)" };
}

// v3.99bj — Score breakdown helpers. These mirror the backend scoring
// functions in /app/backend/routes/forge_index.py exactly so the points
// users see here add up to the same total the API returned.
function _round(n) { return Math.round(n); }

function computeBreederBreakdown(s) {
  const we = Number(s.weighted_evidence) || 0;
  const total = Number(s.total_grows) || 0;
  const verified = Number(s.verified_grows) || 0;
  const rc = Number(s.review_count) || 0;
  const gbr = Number(s.grow_backed_reviews) || 0;
  const listings = Number(s.listing_count) || 0;
  const hr = Number(s.herm_rate) || 0;
  const qualityKeys = ["avg_germination_success", "avg_breeder_accuracy", "avg_satisfaction"];
  const qualitySum = qualityKeys.reduce((acc, k) => {
    const v = s[k];
    if (v == null) return acc;
    return acc + Math.min(((v - 1) / 4) * 5.4, 5.4);
  }, 0);

  const out = [];
  out.push({
    key: "evidence",
    delta: _round(Math.min(we * 1.8, 30)),
    label: "evidence",
    tip: "Weighted grow logs · cap +30. Add live grows + archive runs to raise this.",
  });
  if (total > 0) {
    out.push({
      key: "verified-ratio",
      delta: _round(Math.min(verified / total, 1.0) * 15),
      label: "verified ratio",
      tip: "Share of your grow logs that are breeder-submitted or verified-purchase · cap +15.",
    });
  }
  out.push({
    key: "reviews",
    delta: _round(Math.min(rc * 1.2, 20)),
    label: "reviews",
    tip: "Structured reviews on your storefront · cap +20.",
  });
  if (gbr > 0) {
    out.push({
      key: "grow-backed",
      delta: _round(Math.min(gbr * 1.5, 8)),
      label: "grow-backed",
      tip: "Reviews that link a verified grow log · cap +8. The strongest trust signal.",
    });
  }
  if (qualitySum > 0) {
    out.push({
      key: "quality",
      delta: _round(qualitySum),
      label: "quality avgs",
      tip: "Germination success, breeder accuracy, satisfaction (1–5 averages) · cap +16.",
    });
  }
  out.push({
    key: "catalog",
    delta: _round(Math.min(listings * 0.6, 6)),
    label: "catalog depth",
    tip: "Live listings on your storefront · cap +6.",
  });
  if (hr > 0) {
    out.push({
      key: "herm",
      delta: -_round(Math.min(hr * 24, 12)),
      label: "herm penalty",
      tip: "Reported herm rate from grows · max −12.",
    });
  }
  return out.filter((b) => b.delta !== 0);
}

function computeStrainBreakdown(s) {
  const we = Number(s.weighted_evidence) || 0;
  const total = Number(s.total_grows) || 0;
  const verified = Number(s.verified_grows) || 0;
  const rc = Number(s.review_count) || 0;
  const gbr = Number(s.grow_backed_reviews) || 0;
  const hr = Number(s.herm_rate) || 0;
  const qualityKeys = ["avg_germination_success", "avg_breeder_accuracy", "avg_satisfaction"];
  const qualitySum = qualityKeys.reduce((acc, k) => {
    const v = s[k];
    if (v == null) return acc;
    return acc + Math.min(((v - 1) / 4) * 5.4, 5.4);
  }, 0);

  const out = [];
  out.push({ key: "evidence", delta: _round(Math.min(we * 3.5, 35)), label: "evidence",
             tip: "Weighted grow logs on this strain · cap +35." });
  if (total > 0) {
    out.push({ key: "verified-ratio", delta: _round(Math.min(verified / total, 1.0) * 15),
               label: "verified ratio",
               tip: "Share of grow logs that are verified · cap +15." });
  }
  out.push({ key: "reviews", delta: _round(Math.min(rc * 2, 20)), label: "reviews",
             tip: "Structured reviews · cap +20." });
  if (gbr > 0) {
    out.push({ key: "grow-backed", delta: _round(Math.min(gbr * 2, 8)), label: "grow-backed",
               tip: "Reviews linked to a verified grow log · cap +8." });
  }
  if (qualitySum > 0) {
    out.push({ key: "quality", delta: _round(qualitySum), label: "quality avgs",
               tip: "Germination, accuracy, satisfaction (1–5 averages) · cap +16." });
  }
  if (hr > 0) {
    out.push({ key: "herm", delta: -_round(Math.min(hr * 24, 12)), label: "herm penalty",
               tip: "Reported herm rate · max −12." });
  }
  return out.filter((b) => b.delta !== 0);
}

function Stat({ label, value, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12px]" data-testid={`forge-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="font-mono uppercase tracking-[0.14em]" style={{ color: "var(--text-muted)" }}>
        {label}
      </span>
      <span className="font-mono" style={{ color: accent || "var(--text)" }}>
        {value}
      </span>
    </div>
  );
}

export default function ForgeIndexPanel({ scope = "strain", idOrSlug, compact = false, variant = "full" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idOrSlug) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: resp } = await api.get(SCOPE_PATH[scope](idOrSlug));
        if (!cancelled) setData(resp);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, idOrSlug]);

  if (loading) {
    return (
      <div className="bg-[var(--surface-2)] border border-[var(--border)] p-4 animate-pulse"
           data-testid="forge-index-loading">
        <div className="h-3 w-32 bg-[var(--border)]"/>
      </div>
    );
  }
  if (!data) return null;
  const s = data.signals || {};
  // Hide the panel entirely when there's literally zero supporting data,
  // since a 0-score with all zeros reads as a negative signal rather than
  // "we just don't have data yet."
  const hasData =
    (s.total_grows || 0) > 0 ||
    (s.review_count || 0) > 0;
  if (!hasData) return null;

  const tier = tierFromScore(data.score);

  // v3.99bg — Slim horizontal variant for use directly below the breeder
  // hero card. Compact ticker that only surfaces Forge-Index-unique data
  // (the score, tier, and cultivation/integrity signals). Avoids any stat
  // the breeder card already displays (Rating / Reviews / Listings).
  if (variant === "slim") {
    const cells = [
      { label: "Grows", value: s.total_grows ?? 0 },
      { label: "Verified grows", value: s.verified_grows ?? 0,
        accent: s.verified_grows > 0 ? "var(--primary-text)" : null },
    ];
    if ((s.grow_backed_reviews ?? 0) > 0) {
      cells.push({ label: "Grow-backed reviews", value: s.grow_backed_reviews,
                   accent: "var(--primary-text)" });
    }
    if ((s.herm_rate || 0) > 0) {
      cells.push({ label: "Herm rate",
                   value: `${Math.round((s.herm_rate || 0) * 100)}%`,
                   accent: "#E07A5F" });
    }
    // v3.99bj — Compute the actual point contribution of each component so
    // breeders can see exactly what makes up their score. Math mirrors
    // `_breeder_forge_score` on the backend; keep these in lockstep.
    const breakdown = scope === "breeder" ? computeBreederBreakdown(s) : computeStrainBreakdown(s);
    return (
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] flex flex-col"
           style={{ borderRadius: 2 }}
           data-testid="forge-index-panel-slim"
           title={`Forge Index · ${data.score}/100 — platform authenticity & cultivation-evidence score (0–100). Weighted from grow logs, verified grows, structured reviews, and integrity signals.`}>
        {/* Row 1: score + headline cells */}
        <div className="flex flex-wrap items-stretch">
          {/* Score cell */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-r border-[var(--border)]">
            <Gauge className="w-4 h-4 flex-shrink-0" style={{ color: "var(--gold)" }} />
            <div className="flex items-baseline gap-2">
              <span className="font-display tracking-tighter leading-none"
                    style={{ fontSize: "26px", color: tier.color }}
                    data-testid="forge-index-score">
                {data.score}
              </span>
              <div className="flex flex-col">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] leading-tight"
                      style={{ color: tier.color }}>
                  {tier.label}
                </span>
                <span className="text-[8px] font-mono uppercase tracking-[0.2em] leading-tight"
                      style={{ color: "var(--text-muted)" }}>
                  Forge Index · /100
                </span>
              </div>
            </div>
          </div>
          {cells.map((c, i) => (
            <div key={c.label}
                 className={`flex flex-col justify-center px-3.5 py-2.5 ${i < cells.length - 1 ? "border-r border-[var(--border)]" : ""}`}
                 data-testid={`forge-slim-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <span className="text-[8px] font-mono uppercase tracking-[0.2em] mb-0.5 whitespace-nowrap"
                    style={{ color: "var(--text-muted)" }}>
                {c.label}
              </span>
              <span className="font-mono text-sm leading-tight"
                    style={{ color: c.accent || "var(--text)" }}>
                {c.value}
              </span>
            </div>
          ))}
        </div>
        {/* Row 2: one-liner explainer */}
        <div className="px-4 py-2 border-t border-[var(--border)] text-[10.5px] leading-snug"
             style={{ color: "var(--text-2)" }}
             data-testid="forge-index-explainer">
          <span className="font-mono uppercase tracking-[0.18em] mr-1"
                style={{ color: "var(--gold)" }}>What is this?</span>
          {scope === "breeder"
            ? "Authenticity score earned from verified cultivation evidence — grow logs, structured reviews, catalog depth, and low herm rates. Higher tiers unlock storefront badges and rotation in editorial features."
            : "Strain authenticity score earned from verified grow logs and structured reviews on this cultivar. Documented evidence raises the score; unresolved herm signals lower it."}
        </div>
        {/* Row 3: point breakdown */}
        {breakdown.length > 0 && (
          <div className="px-3 py-2 border-t border-[var(--border)] flex flex-wrap gap-x-3 gap-y-1 items-center"
               data-testid="forge-index-breakdown">
            <span className="text-[9px] font-mono uppercase tracking-[0.2em] mr-1"
                  style={{ color: "var(--text-muted)" }}>
              Score breakdown
            </span>
            {breakdown.map((b, i) => (
              <span key={i}
                    title={b.tip}
                    className="text-[10px] font-mono whitespace-nowrap"
                    style={{ color: b.delta < 0 ? "#E07A5F" : "var(--text-2)" }}
                    data-testid={`forge-breakdown-${b.key}`}>
                <span style={{ color: b.delta < 0 ? "#E07A5F" : "var(--primary-text)" }}>
                  {b.delta >= 0 ? "+" : "−"}{Math.abs(b.delta)}
                </span>{" "}
                {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[var(--surface-2)] border border-[var(--border)] p-4"
         data-testid="forge-index-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4" style={{ color: "var(--gold)" }} />
          <span className="label-eyebrow" style={{ color: "var(--gold)" }}>
            Forge Index · {scope === "breeder" ? "Breeder" : "Strain"}
          </span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em]"
              style={{ color: "var(--text-muted)" }}>
          Live · 0–100
        </span>
      </div>

      <div className="flex items-baseline gap-3 mb-4">
        <div className="font-display tracking-tighter leading-none"
             style={{ fontSize: "44px", color: tier.color }}
             data-testid="forge-index-score">
          {data.score}
        </div>
        <div>
          <div className="text-[12px] font-mono uppercase tracking-[0.18em]"
               style={{ color: tier.color }}>
            {tier.label}
          </div>
          <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            Authenticity + cultivation evidence score
          </div>
        </div>
      </div>

      {!compact && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 pt-3 border-t border-[var(--border)]">
          <Stat label="Total grow logs" value={s.total_grows ?? 0} />
          <Stat label="Verified grows"
                value={s.verified_grows ?? 0}
                accent={s.verified_grows > 0 ? "var(--primary-text)" : null} />
          <Stat label="Weighted evidence" value={(s.weighted_evidence ?? 0).toFixed(1)} />
          <Stat label="Herm reports"
                value={`${s.herm_grows ?? 0}${s.total_grows ? ` · ${Math.round((s.herm_rate || 0) * 100)}%` : ""}`}
                accent={s.herm_grows > 0 ? "#E07A5F" : null} />
          <Stat label="Approved reviews" value={s.review_count ?? 0} />
          <Stat label="Grow-backed reviews"
                value={s.grow_backed_reviews ?? 0}
                accent={s.grow_backed_reviews > 0 ? "var(--primary-text)" : null} />
          {s.avg_germination_success != null && (
            <Stat label="Avg germination" value={`${s.avg_germination_success}/5`} />
          )}
          {s.avg_breeder_accuracy != null && (
            <Stat label="Avg breeder accuracy" value={`${s.avg_breeder_accuracy}/5`} />
          )}
          {s.avg_satisfaction != null && (
            <Stat label="Avg satisfaction" value={`${s.avg_satisfaction}/5`} />
          )}
          {scope === "breeder" && (
            <Stat label="Active listings" value={s.listing_count ?? 0} />
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[var(--border)] flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em]"
           style={{ color: "var(--text-muted)" }}>
        <Sprout className="w-3 h-3" />
        <span>Grow logs</span>
        <span>·</span>
        <ShieldCheck className="w-3 h-3" />
        <span>Verified evidence</span>
        <span>·</span>
        <TrendingUp className="w-3 h-3" />
        <span>Structured reviews</span>
        {(s.herm_rate || 0) > 0 && (
          <>
            <span>·</span>
            <AlertTriangle className="w-3 h-3" style={{ color: "#E07A5F" }} />
            <span>Herm signal</span>
          </>
        )}
      </div>
    </div>
  );
}
